import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { shippingApi, ordersApi } from '@/services/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { usePricing } from '@/hooks/usePricing';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Loader2, Info, Truck, Store } from 'lucide-react';
import { toast } from 'sonner';

const HANDLING_FEE = 15;
const VAT_RATE = 0.25;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, fetchCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { isBusiness, priceExclVat, vatAmount: calcVat } = usePricing();
  const [loading, setLoading] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');
  const [shippingCost, setShippingCost] = useState(0);
  const [estimatedDeliveryDays, setEstimatedDeliveryDays] = useState(3);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: user?.email || '',
    phone: '', address: '', postalCode: '', city: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Du måste vara inloggad för att gå till kassan');
      navigate('/logga-in');
      return;
    }
    fetchCart();
  }, [isAuthenticated, navigate, fetchCart]);

  useEffect(() => {
    if (cart.items.length > 0 && window.trackInitiateCheckout) {
      window.trackInitiateCheckout({ items: cart.items, total: cart.total || cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0) });
    }
  }, [cart.items, cart.total]);

  useEffect(() => {
    if (user?.email) setFormData(prev => ({ ...prev, email: user.email }));
  }, [user]);

  useEffect(() => {
    const calculateShipping = async () => {
      if (deliveryMethod === 'pickup' || cart.items.length === 0) { setShippingCost(0); return; }
      try {
        const response = await shippingApi.calculate({
          products: cart.items.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
          delivery_method: deliveryMethod
        });
        setShippingCost(response.data.shipping_cost || 0);
        setEstimatedDeliveryDays(response.data.estimated_delivery_days || 3);
      } catch {
        setShippingCost(cart.items.length * 79);
      }
    };
    calculateShipping();
  }, [cart.items, deliveryMethod]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) { toast.error('Din varukorg är tom'); navigate('/varukorg'); return; }

    const customerName = `${formData.firstName} ${formData.lastName}`.trim();
    const shippingAddress = {
      name: customerName,
      street: formData.address,
      postal_code: formData.postalCode,
      city: formData.city,
      phone: formData.phone,
    };

    setLoading(true);
    try {
      const payload = {
        items: cart.items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
        })),
        delivery_method: deliveryMethod,
        customer_name: customerName,
        customer_email: formData.email,
        customer_phone: formData.phone,
        shipping_address: shippingAddress,
        billing_address: shippingAddress,
      };

      const { data: order } = await ordersApi.createWithShipping(payload);
      toast.success('Beställning mottagen');
      navigate(`/checkout/success?order_id=${order.id}`);
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Kunde inte lägga beställningen');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = cart.total || cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const shippingTotal = deliveryMethod === 'pickup' ? 0 : shippingCost;
  const handlingFee = cart.items.length > 0 ? HANDLING_FEE : 0;
  const grossTotal = subtotal + shippingTotal + handlingFee;
  const vatAmount = grossTotal * VAT_RATE / (1 + VAT_RATE);
  const netTotal = grossTotal - vatAmount;

  const breadcrumbs = [{ label: 'Varukorg', href: '/varukorg' }, { label: 'Kassa' }];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="checkout-page">
      <div className="container-main py-8">
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-2xl md:text-3xl font-black text-[#0F172A] mb-8">Kassa</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={handleSubmitOrder}>
              <div className="bg-white rounded-md shadow-sm p-6 mb-6">
                <h2 className="text-lg font-bold text-[#0F172A] mb-6">Kunduppgifter</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="firstName">Förnamn *</Label><Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="mt-1" data-testid="input-firstName" /></div>
                  <div><Label htmlFor="lastName">Efternamn *</Label><Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required className="mt-1" data-testid="input-lastName" /></div>
                  <div><Label htmlFor="email">E-post *</Label><Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required className="mt-1" data-testid="input-email" /></div>
                  <div><Label htmlFor="phone">Telefon *</Label><Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required className="mt-1" data-testid="input-phone" /></div>
                </div>
              </div>

              <div className="bg-white rounded-md shadow-sm p-6 mb-6">
                <h2 className="text-lg font-bold text-[#0F172A] mb-6">Leveransadress</h2>
                <div className="space-y-4">
                  <div><Label htmlFor="address">Gatuadress *</Label><Input id="address" name="address" value={formData.address} onChange={handleInputChange} required className="mt-1" data-testid="input-address" /></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label htmlFor="postalCode">Postnummer *</Label><Input id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleInputChange} required className="mt-1" data-testid="input-postalCode" /></div>
                    <div><Label htmlFor="city">Ort *</Label><Input id="city" name="city" value={formData.city} onChange={handleInputChange} required className="mt-1" data-testid="input-city" /></div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-md shadow-sm p-6 mb-6">
                <h2 className="text-lg font-bold text-[#0F172A] mb-4">Leveransmetod</h2>
                {cart.items.some(item => item.requires_shipping_quote) && (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-yellow-800">Kontakta varuhuset för fraktförfrågan</p>
                        <p className="text-sm text-yellow-700 mt-1">En eller flera varor kräver offert för frakt.</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  <label className={`flex items-start gap-4 p-4 rounded-md border-2 cursor-pointer transition-colors ${deliveryMethod === 'shipping' ? 'border-[#003B73] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="delivery-shipping">
                    <input type="radio" name="deliveryMethod" value="shipping" checked={deliveryMethod === 'shipping'} onChange={(e) => setDeliveryMethod(e.target.value)} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2"><Truck className="h-5 w-5 text-[#003B73]" /><span className="font-semibold text-[#0F172A]">Hemleverans</span></div>
                      <p className="text-sm text-gray-500 mt-1">Leverans inom {estimatedDeliveryDays} arbetsdagar.</p>
                      {shippingCost > 0 && <p className="text-sm font-semibold text-[#003B73] mt-1">Frakt: {shippingCost.toLocaleString('sv-SE')} kr</p>}
                    </div>
                  </label>
                  <label className={`flex items-start gap-4 p-4 rounded-md border-2 cursor-pointer transition-colors ${deliveryMethod === 'pickup' ? 'border-[#003B73] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="delivery-pickup">
                    <input type="radio" name="deliveryMethod" value="pickup" checked={deliveryMethod === 'pickup'} onChange={(e) => setDeliveryMethod(e.target.value)} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2"><Store className="h-5 w-5 text-[#FF8A00]" /><span className="font-semibold text-[#0F172A]">Hämta i butik</span><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">Gratis</span></div>
                      <p className="text-sm text-gray-500 mt-1">Hämta din beställning i butiken.</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-md shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#0F172A] mb-4">Betalning</h2>
                <p className="text-sm text-gray-600">
                  Betalning sker via ByggFynd (Kustom). Efter att du skickat beställningen följer du betalinstruktionerna du får från oss — ingen kortbetalning på denna sida.
                </p>
              </div>

              <div className="lg:hidden mt-6">
                <Button type="submit" disabled={loading || cart.items.length === 0} className="w-full h-14 bg-[#FF8A00] hover:bg-[#e67c00] text-white font-bold text-lg rounded-sm" data-testid="submit-order-btn-mobile">
                  {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Skickar beställning...</> : <><Lock className="mr-2 h-5 w-5" />Skicka beställning</>}
                </Button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-md shadow-sm p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="text-lg font-bold text-[#0F172A] mb-6">Din beställning</h2>

              <div className="space-y-4 mb-6">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-[#0F172A] line-clamp-1">{item.title}</p>
                      <p className="text-gray-500">{item.quantity} st</p>
                    </div>
                    <span className="font-semibold whitespace-nowrap">{(item.item_total || item.price * item.quantity).toLocaleString('sv-SE')} kr</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-3 text-sm">
                {isBusiness ? (
                  <>
                    <div className="flex justify-between"><span className="text-gray-600">Produkter exkl. moms</span><span className="font-semibold">{priceExclVat(subtotal).toLocaleString('sv-SE')} kr</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">{deliveryMethod === 'pickup' ? 'Frakt (hämtas i butik)' : 'Frakt exkl. moms'}</span><span className={`font-semibold ${deliveryMethod === 'pickup' ? 'text-green-600' : ''}`}>{deliveryMethod === 'pickup' ? 'Gratis' : `${priceExclVat(shippingTotal).toLocaleString('sv-SE')} kr`}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Expeditionsavgift exkl. moms</span><span className="font-semibold">{priceExclVat(handlingFee).toLocaleString('sv-SE')} kr</span></div>
                    <div className="border-t border-gray-100 pt-3"><div className="flex justify-between font-medium"><span className="text-gray-700">Summa exkl. moms</span><span className="font-bold">{priceExclVat(grossTotal).toLocaleString('sv-SE')} kr</span></div></div>
                    <div className="flex justify-between"><span className="text-gray-600">Moms (25%)</span><span className="font-semibold" data-testid="vat-amount">{calcVat(grossTotal).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</span></div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-gray-600">Produkter</span><span className="font-semibold">{subtotal.toLocaleString('sv-SE')} kr</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">{deliveryMethod === 'pickup' ? 'Frakt (hämtas i butik)' : 'Frakt'}</span><span className={`font-semibold ${deliveryMethod === 'pickup' ? 'text-green-600' : ''}`}>{deliveryMethod === 'pickup' ? 'Gratis' : `${shippingTotal.toLocaleString('sv-SE')} kr`}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Expeditionsavgift</span><span className="font-semibold">{handlingFee} kr</span></div>
                  </>
                )}
              </div>

              <div className="border-t border-gray-100 mt-4 pt-4">
                {!isBusiness && (
                  <div className="bg-blue-50 rounded-md p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-800">
                        <p className="font-semibold mb-1">Momsspecifikation (25%)</p>
                        <div className="space-y-1">
                          <div className="flex justify-between"><span>Belopp exkl. moms:</span><span>{netTotal.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</span></div>
                          <div className="flex justify-between font-semibold"><span>Varav moms (25%):</span><span data-testid="vat-amount-detail">{vatAmount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-[#0F172A]">Att betala</span>
                  <span className="text-2xl font-black text-[#003B73]" data-testid="checkout-total">{grossTotal.toLocaleString('sv-SE')} kr</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{isBusiness ? 'Inkl. moms' : `Inkl. moms ${vatAmount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`}</p>
              </div>

              <Button
                type="button"
                onClick={() => {
                  const form = document.querySelector('[data-testid="checkout-page"] form');
                  if (form) form.requestSubmit();
                }}
                disabled={loading || cart.items.length === 0}
                className="hidden lg:flex w-full mt-6 h-14 bg-[#FF8A00] hover:bg-[#e67c00] text-white font-bold text-lg rounded-sm items-center justify-center"
                data-testid="submit-order-btn"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Skickar beställning...</>
                ) : (
                  <><Lock className="mr-2 h-5 w-5" />Skicka beställning</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
